import { Core } from "@faire/design-tokens";
import { trackSeoExplorePageProductGridBrandLinkClick } from "@faire/web-api/events/seo/click/explorePageProductGridBrandLink";
import { trackSeoExplorePageProductGridProductLinkClick } from "@faire/web-api/events/seo/click/explorePageProductGridProductLink";
import { IBadge } from "@faire/web-api/indigofair/badges/IBadge";
import { IClickInfo } from "@faire/web-api/indigofair/data/IClickInfo";
import { ILeanProductTile } from "@faire/web-api/indigofair/data/ILeanProductTile";
import {
  localNum,
  useStrictLocalization,
} from "@faire/web/common/localization";
import { isPrerender } from "@faire/web/common/user-agent/isPrerender";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import capitalize from "lodash/capitalize";
import React, { useRef } from "react";

import { useAppSignUpPageResultsUpdateEffect } from "@faire/retailer/components/AppSignUp/AppSignUpPageResultsProvider";
import { ExploreBanners } from "@faire/retailer/components/Banners/ExploreBanners/ExploreBanners";
import { EfficientListLoader } from "@faire/retailer/components/EfficientListLoader/EfficientListLoader";
import { LoadingProductTileSkeleton } from "@faire/retailer/components/LoadingProductTileSkeleton/LoadingProductTileSkeleton";
import {
  ROWS_PER_BATCH,
  SingleProductRowPlaceholder,
} from "@faire/retailer/components/SearchProducts/SingleProductRowPlaceholder";
import {
  handleGenerateBrandUrl,
  handleGenerateProductLinkProps,
  ITEMS_PER_ROW_RESPONSIVE,
} from "@faire/retailer/components/SearchProducts/utils";
import { handleShowroomProductBadgeViewed } from "@faire/retailer/components/Showroom/ShowroomBadgeUtils";
import { TrackedLinkContext } from "@faire/retailer/components/TrackedLink";
import { PAGE_TYPE } from "@faire/retailer/consts/PAGE_TYPE";
import { PromotedBadge } from "@faire/retailer/features/FaireDirectRewards/PromotedBadge";
import { shouldShowPromotedBadge } from "@faire/retailer/features/FaireDirectRewards/util";
import { getAltTag } from "@faire/retailer/features/Marketplace/utils";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { brandUrlParametersILeanProductTileAdapter } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/useBrandUrl";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { productUrlParametersILeanProductTileAdapter } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/useProductLinkProps";
import { MarketplaceProduct } from "@faire/retailer/features/MarketplaceItems/MarketplaceProduct/MarketplaceProduct";
import { SaveToBoardProvider } from "@faire/retailer/features/RetailerBoards/Components/SaveToBoard";
import { isLoggedInRetailer } from "@faire/retailer/lib/isLoggedInRetailer";
import { assignSettingOnEvent } from "@faire/retailer/settings/assignSettingOnEvent";
import { ExplorePageCTA } from "@faire/retailer/views/explore-page/__internal__/ExplorePageCTA";
import {
  SearchListWrapper,
  TileWrapper,
} from "@faire/retailer/views/explore-page/__internal__/ExploreStyles";
import { NoExactResultsContainer } from "@faire/retailer/views/explore-page/__internal__/NoExactResultsContainer";
import { useExplorePageGetProductTiles } from "@faire/retailer/views/explore-page/__internal__/useExplorePageGetProductTiles";
import { useExplorePageProductsQuery } from "@faire/retailer/views/explore-page/__internal__/useExplorePageProductsQuery";
import { useExplorePageSearchTerm } from "@faire/retailer/views/explore-page/__internal__/useExplorePageSearchTerm";
import { useExplorePageShowCTAAndInlineFilter } from "@faire/retailer/views/explore-page/__internal__/useExplorePageShowCTAAndInlineFilter";

interface ExploreProductsResultsListProps {
  title: string;
  loadTimingRef?: React.Ref<HTMLDivElement>;
}

export const ExploreProductsResultsList: React.FC<
  ExploreProductsResultsListProps
> = ({ title, loadTimingRef }) => {
  const { productSearchString } = useExplorePageSearchTerm();
  const { strictLocalize } = useStrictLocalization();
  const badgeViewedProductTokens = useRef<Set<string>>(new Set<string>());
  const {
    requestId,
    brandMap,
    totalPrimaryResults,
    isSearchPending,
    products: rawProductTiles,
  } = useExplorePageProductsQuery();

  useAppSignUpPageResultsUpdateEffect({ productTiles: rawProductTiles });

  const shouldShowCTAndInlineFilters = useExplorePageShowCTAAndInlineFilter();
  const onProductBadgeVisible = (
    productToken?: string,
    topLeftTileBadge?: IBadge
  ) => {
    if (!productToken || badgeViewedProductTokens.current.has(productToken)) {
      return;
    }
    badgeViewedProductTokens.current.add(productToken);
    handleShowroomProductBadgeViewed(productToken, requestId, topLeftTileBadge);
  };
  const products = useExplorePageGetProductTiles();

  const renderProduct = (productTile: ILeanProductTile | undefined) => {
    if (!productTile) {
      return <LoadingProductTileSkeleton />;
    }

    const brand = brandMap[productTile?.product?.brand_token ?? ""];
    const showPromotedBadge = shouldShowPromotedBadge(
      productTile?.product?.is_promoted,
      productTile?.product?.promoted_by ?? []
    );
    const templateAltTag = getAltTag(
      {
        pageType: PAGE_TYPE.DISCOVER,
        title,
        productName: productTile?.product?.name,
      },
      strictLocalize
    );

    return (
      <OnViewContainer productTile={productTile}>
        <MarketplaceProduct
          altTag={templateAltTag}
          productTile={productTile}
          brand={brand}
          onClick={handleOnProductClick(productTile?.product)}
          onBrandClick={handleOnBrandClick(productTile?.product, brand)}
          generateProductLinkProps={handleGenerateProductLinkProps(
            productSearchString,
            productUrlParametersILeanProductTileAdapter(productTile),
            []
          )}
          generateBrandUrl={handleGenerateBrandUrl(
            productSearchString,
            brandUrlParametersILeanProductTileAdapter(productTile)
          )}
          onProductVisible={onProductBadgeVisible}
          shouldShowPromotedBadge={showPromotedBadge}
        />
        {showPromotedBadge && !isLoggedInRetailer() ? (
          <PromotedBadge promotedBy={productTile?.product?.promoted_by} />
        ) : (
          <Spacer size="2x" />
        )}
      </OnViewContainer>
    );
  };

  const renderPlaceholder = () => (
    <SingleProductRowPlaceholder
      itemsPerRowResponsive={ITEMS_PER_ROW_RESPONSIVE}
    />
  );
  const itemsPerRow = useResponsiveValue(ITEMS_PER_ROW_RESPONSIVE);

  const handleOnProductClick =
    (product: ILeanProductTile.ILeanProduct | undefined) => () => {
      if (!product?.token || !productSearchString) {
        return;
      }
      trackSeoExplorePageProductGridProductLinkClick(product?.token);
    };

  const handleOnBrandClick =
    (
      product: ILeanProductTile.ILeanProduct | undefined,
      brand: ILeanProductTile.IBrand | undefined
    ) =>
    () => {
      if (!productSearchString || !product?.token || !brand?.token) {
        return;
      }
      trackSeoExplorePageProductGridBrandLinkClick(brand.token, product?.token);
    };

  if (!isSearchPending && totalPrimaryResults === 0) {
    return <NoExactResultsContainer loadTimingRef={loadTimingRef} />;
  }

  return (
    <>
      <ExploreProductsGridHeader
        totalPrimaryResults={totalPrimaryResults}
        isLoading={isSearchPending}
      />
      <SearchListWrapper $columns={itemsPerRow} ref={loadTimingRef}>
        <TrackedLinkContext.Provider
          value={{
            type: IClickInfo.Type.PRODUCT,
            containerName: "explore-page-product-results-container",
            requestId,
          }}
        >
          <SaveToBoardProvider value={{ trackOrigin: "search" }}>
            <EfficientListLoader
              data={products}
              batchSize={itemsPerRow * ROWS_PER_BATCH}
              renderComponent={renderProduct}
              forceRender={isPrerender()}
              placeholderFn={renderPlaceholder}
              threshold={-400}
              hashKey={requestId}
            />
          </SaveToBoardProvider>
        </TrackedLinkContext.Provider>
        {shouldShowCTAndInlineFilters ? (
          <ExploreBanners productSearchString={productSearchString} />
        ) : null}
      </SearchListWrapper>

      {shouldShowCTAndInlineFilters ? <ExplorePageCTA /> : null}
      <Spacer height="3x" />
    </>
  );
};

const ExploreProductsGridHeader: React.FC<{
  totalPrimaryResults: number;
  isLoading: boolean;
}> = ({ totalPrimaryResults, isLoading }) => {
  const { productSearchString } = useExplorePageSearchTerm();
  const header = capitalize(productSearchString);
  if (isLoading) {
    return null;
  }
  return (
    <>
      <Typography variant="paragraphSansRegular" color={Core.text.subdued}>
        <StrictLocalMsg
          defaultMessage="{resultsNumber} results"
          description={{
            text: "subheader of the explore page, {resultsNumber} being the number of results",
          }}
          values={{ resultsNumber: localNum(totalPrimaryResults) }}
        />
      </Typography>
      <Typography variant="pageHeaderSerifRegular">{header}</Typography>
      <Spacer height="2x" />
    </>
  );
};

const OnViewContainer: React.FC<
  React.PropsWithChildren<{ productTile: ILeanProductTile }>
> = ({ children, productTile }) => {
  const onViewRef = useDoActionWhenVisible(() => {
    const settings = productTile?.settings_to_assign_on_view;
    if (settings && settings.length > 0) {
      settings.forEach((setting) => assignSettingOnEvent(setting));
    }
  });

  return <TileWrapper ref={onViewRef}>{children}</TileWrapper>;
};
